import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { clearToken } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=auth
  const [loading, setLoading] = useState(true);
  const { applyServerTheme } = useTheme();

  useEffect(() => {
    if (user && user.theme) applyServerTheme(user.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Analytics identity: founders never pollute the stats.
  useEffect(() => {
    const ph = window.posthog;
    if (!ph || !user) return;
    if (user.is_admin) {
      ph.opt_out_capturing?.();
      return;
    }
    ph.identify?.(user.user_id, {
      plan: user.entitlement?.is_pro ? "pro" : "free",
      onboarded: !!user.onboarded,
      auth_provider: user.auth_provider,
    });
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    window.posthog?.reset?.();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
